import { useTranslations } from 'next-intl';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { AgentLineScript } from '@/app/components/icons/AgentLineScript';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@/app/components/ui/hover-card';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { openDownloadUrl } from '../../../_utils';
import { getDownloadUrl } from '../../../apis';
import type { ClawContentType } from '../../../types';
import { getPreviewTypeFromPath, isTextPreviewFile } from '../../File/utils';

const HOVER_PREVIEW_SIZE = 240;

interface HoverPreviewProps {
  fileType: 'image' | 'video';
  previewUrl: string;
}

const HoverPreview = ({ fileType, previewUrl }: HoverPreviewProps) => {
  const [isReady, setIsReady] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setIsReady(false);
    setSize(null);
  }, [previewUrl]);

  return (
    <div
      className="relative flex items-center justify-center overflow-hidden rounded-8 bg-system-black36"
      style={{ width: HOVER_PREVIEW_SIZE, height: HOVER_PREVIEW_SIZE }}
    >
      {fileType === 'image' ? (
        size ? (
          <MediaWithWatermark
            width={size.width}
            height={size.height}
            className="relative h-full w-auto"
            showWatermark={SITE === 'china'}
          >
            <img
              alt=""
              src={previewUrl}
              className={cn('size-full object-contain transition-opacity', isReady ? 'opacity-100' : 'opacity-0')}
              decoding="async"
              onLoad={() => setIsReady(true)}
              onError={() => setIsReady(false)}
            />
          </MediaWithWatermark>
        ) : (
          <img
            alt=""
            src={previewUrl}
            className={cn(
              'max-h-full max-w-full object-contain transition-opacity',
              isReady ? 'opacity-100' : 'opacity-0',
            )}
            decoding="async"
            onLoad={(event) => {
              const target = event.currentTarget;
              if (target.naturalWidth && target.naturalHeight) {
                setSize({ width: target.naturalWidth, height: target.naturalHeight });
              }
              setIsReady(true);
            }}
            onError={() => setIsReady(false)}
          />
        )
      ) : size ? (
        <MediaWithWatermark
          width={size.width}
          height={size.height}
          className="relative h-full w-auto"
          showWatermark={SITE === 'china'}
        >
          <video
            ref={videoRef}
            className={cn('size-full object-contain transition-opacity', isReady ? 'opacity-100' : 'opacity-0')}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            src={previewUrl}
            onLoadedData={() => setIsReady(true)}
            onError={() => setIsReady(false)}
          >
            <track kind="captions" />
          </video>
        </MediaWithWatermark>
      ) : (
        <video
          ref={videoRef}
          className={cn(
            'max-h-full max-w-full object-contain transition-opacity',
            isReady ? 'opacity-100' : 'opacity-0',
          )}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          src={previewUrl}
          onLoadedMetadata={() => {
            const video = videoRef.current;
            if (video?.videoWidth && video.videoHeight) {
              setSize({ width: video.videoWidth, height: video.videoHeight });
            }
          }}
          onLoadedData={() => setIsReady(true)}
          onError={() => setIsReady(false)}
        >
          <track kind="captions" />
        </video>
      )}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center text-system-white40">
          {fileType === 'image' ? <ProductLinePic className="size-8" /> : <ProductLineAiVideo className="size-8" />}
        </div>
      )}
    </div>
  );
};

export const FileItem = ({
  file_path,
  file_type,
  status,
  onDelete,
  onPreview,
}: {
  file_path: string;
  file_type?: ClawContentType;
  status?: string;
  onDelete?: () => void;
  onPreview?: (filePath: string) => void;
}) => {
  const t = useTranslations();
  const clawId = useStore((state) => state.activeClaw?.id);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const requestedRef = useRef(false);

  const isHoverable = (file_type === 'image' || file_type === 'video') && status !== 'deleted';

  useEffect(() => {
    requestedRef.current = false;
    setPreviewUrl(null);
  }, [clawId, file_path]);

  const handleHoverOpenChange = (open: boolean) => {
    if (!open || !isHoverable || !clawId || requestedRef.current) return;
    requestedRef.current = true;
    getDownloadUrl({
      query: {
        claw_id: clawId,
        file_path,
      },
    }).then(({ data, error }) => {
      if (error || !data?.download_url) {
        requestedRef.current = false;
        return;
      }
      setPreviewUrl(data.download_url);
    });
  };

  const handleClick = async () => {
    if (status === 'deleted') {
      toast({
        type: 'error',
        message: t('claw.file.fileDeleted'),
      });
      return;
    }
    const previewType = getPreviewTypeFromPath(file_path);
    const canPreviewAsFile = isTextPreviewFile(file_path) || previewType === 'pdf';
    if (file_type === 'file' && !canPreviewAsFile) {
      if (!clawId) return;
      const { data, error } = await getDownloadUrl({ query: { claw_id: clawId, file_path } });
      if (error || !data?.download_url) {
        toast({ type: 'error', message: t('claw.file.downloadFailed') });
        return;
      }
      openDownloadUrl(data.download_url);
      return;
    }
    onPreview?.(file_path);
  };

  const handleDelete = (event: MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    onDelete?.();
  };

  const trigger = (
    <div
      className="group flex cursor-pointer items-center gap-1 rounded-8 border border-system-white08 bg-[rgba(255,255,255,0.1)] light:bg-[rgba(0,0,0,0.08)] px-2 py-1 text-xs"
      onClick={handleClick}
    >
      {file_type === 'image' && <ProductLinePic className="size-4" />}
      {file_type === 'video' && <ProductLineAiVideo className="size-4" />}
      {file_type === 'audio' && <BaseLineMusic className="size-4" />}
      {file_type === 'file' && <AgentLineScript className="size-4" />}
      <div>{file_path}</div>
      {onDelete && (
        <BaseLineClose
          className="pointer-events-none hidden size-3 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:block group-hover:opacity-100"
          onClick={handleDelete}
        />
      )}
    </div>
  );

  if (!isHoverable) return trigger;

  return (
    <HoverCard openDelay={200} closeDelay={100} onOpenChange={handleHoverOpenChange}>
      <HoverCardTrigger asChild={true}>{trigger}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent side="top" align="start" className="border-none bg-system-bg02 p-2">
          {previewUrl ? (
            <HoverPreview fileType={file_type as 'image' | 'video'} previewUrl={previewUrl} />
          ) : (
            <div
              className="flex items-center justify-center rounded-8 bg-system-black36 text-system-white40"
              style={{ width: HOVER_PREVIEW_SIZE, height: HOVER_PREVIEW_SIZE }}
            >
              {file_type === 'image' ? (
                <ProductLinePic className="size-8" />
              ) : (
                <ProductLineAiVideo className="size-8" />
              )}
            </div>
          )}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
